/**
 * Light, dark, or whatever the phone is set to.
 *
 * Opt-in, and off by default: the design is deliberately white and near-black,
 * and a phone in dark mode should not silently redraw it. "System" is a choice
 * somebody makes, not the starting point.
 *
 * Stored beside the language, read before first paint from index.html so the
 * screen never flashes white on its way to dark.
 */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'fm.theme';

export function getTheme(): Theme {
  const raw = localStorage.getItem(KEY);
  return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : 'light';
}

/** What the chosen theme resolves to right now. */
export function resolvedTheme(theme: Theme = getTheme()): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme = getTheme()): void {
  document.documentElement.dataset.theme = resolvedTheme(theme);
}

export function setTheme(theme: Theme): void {
  localStorage.setItem(KEY, theme);
  applyTheme(theme);
}

/**
 * Follow the phone while "System" is chosen. Without this, a phone that
 * switches at sunset leaves the app on whatever it was at load.
 */
export function watchSystemTheme(): void {
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (getTheme() === 'system') applyTheme('system');
  });
}
